import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { cloneDeep } from 'lodash'

import {
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Fab,
  Tooltip,
} from '@mui/material'

import { Add } from '@mui/icons-material'
import FormLabel from '@mui/material/FormLabel'
import { addKeyToObjectsArray } from 'utils/addUniqueKeyToObjects'
import { removeAttachment } from 'utils/attachmentOperations'
import AddAttachments from 'components/AddAttachments'
import { useSelector } from 'react-redux'
import { hasAnswerChanged, isValidHttpUrl } from 'utils/functions'
import { OPERATION_TYPES } from 'utils/constants/operationTypes'

import styles from './styles'
import AnswerCard from '../AnswerCard'
import QuestionButton from '../QuestionButton'
import AttachmentsList from '../../../AttachmentsList'

export default function QuestionSixteen(props) {
  const { questionData, saveResponses, formOwner } = props
  const classes = styles()
  const token = useSelector((state) => state.auth.access_token)

  const initialData = {
    subject: '',
    media_link: '',
    envolvement_resume: '',
    attachments: [],
  }

  const initialErrors = {
    subject: false,
    media_link: false,
    envolvement_resume: false,
    attachments: false,
  }

  const initializeFormState = (data) => {
    return {
      hasNegativeMedia: data.items.length > 0,
      items: data.items,
    }
  }

  const [formState, setFormState] = useState(initializeFormState(questionData))

  const [activityData, setActivityData] = useState(initialData)

  const [activityErrors, setActivityErrors] = useState(initialErrors)

  const [attachmentsToRemove, setAttachmentsToRemove] = useState([])

  const [loading, setLoading] = useState(false)

  const [edited, setEdited] = useState(false)
  const [saved, setSaved] = useState(questionData.has_answer)

  const handleFieldChange = (event) => {
    const key = event.target.id
    const newActivityData = cloneDeep(activityData)
    newActivityData[key] = event.target.value
    setActivityData(newActivityData)
  }

  const handleRadioChange = (event) => {
    const newFormState = cloneDeep(formState)
    newFormState.hasNegativeMedia = event.target.value === 'yes'
    setFormState(newFormState)
  }

  const hasNegativeMedia = () => {
    return !(formState.items.length === 0)
  }

  const validateRequiredFields = () => {
    const newActivityErrors = initialErrors

    Object.keys(initialErrors).forEach((key) => {
      if (
        key === 'attachments' &&
        activityData[key].length === 0 &&
        activityData.envolvement_resume === ''
      ) {
        newActivityErrors[key] = true
        return
      }
      if (
        key === 'envolvement_resume' &&
        activityData.attachments.length === 0 &&
        activityData.envolvement_resume === ''
      ) {
        newActivityErrors[key] = true
        return
      }
      if (
        key === 'media_link' &&
        activityData[key] !== '' &&
        !isValidHttpUrl(activityData[key])
      ) {
        toast.error(
          'Links devem conter ao menos o protocolo e domínio. Exemplo: "https://exemplo.com/"',
        )
        newActivityErrors[key] = true
      }
      if (activityData[key] === '' && key !== 'envolvement_resume') {
        newActivityErrors[key] = true
      }
    })

    setActivityErrors(newActivityErrors)

    return !Object.keys(newActivityErrors).some((key) => newActivityErrors[key])
  }

  const handleValidateAndUpdateState = () => {
    if (validateRequiredFields()) {
      const newFormState = cloneDeep(formState)
      newFormState.items.push(activityData)
      setFormState(newFormState)
      setActivityData(initialData)
    } else {
      toast.warn(
        'Favor, verifique se todos os campos obrigatórios foram preenchidos corretamente',
      )
      if (activityErrors.envolvement_resume && activityErrors.attachments) {
        toast.error(
          'Ao preencher este formulário deve-se preencher ao menos o resumo do envolvimento ou anexar um documento',
        )
      }
    }
  }

  const handleRemoveActivity = async (position) => {
    const newFormState = cloneDeep(formState)
    const attachmentsToDelete = newFormState.items[position].attachments

    setAttachmentsToRemove([...attachmentsToRemove, ...attachmentsToDelete])

    newFormState.items = newFormState.items.filter(
      (activity, index) => index !== position,
    )
    setFormState(newFormState)
  }

  const handleRemoveExcludedAttachments = async (attachments) => {
    Promise.all(
      attachments.map((attachment) => removeAttachment(attachment.uuid, token)),
    ).then(() => {
      setAttachmentsToRemove([])
    })
  }

  const handleUpdateAttachments = (attachments) => {
    const newActivityData = cloneDeep(activityData)
    newActivityData.attachments = attachments
    setActivityData(newActivityData)
  }

  const validateAndSave = async (skipValidation = false) => {
    if (formState.items.length > 0 && formState.hasNegativeMedia === false) {
      toast.warn(
        'Você deve excluir as atividades antes de atualizar as respostas.',
      )
    }
    const newQuestionData = cloneDeep(questionData)
    if (!skipValidation && formState.hasNegativeMedia && !hasNegativeMedia()) {
      toast.error(
        'Ao selecionar "Sim" deve-se adicionar ao menos uma atividade',
      )
    } else {
      newQuestionData.has_answer = true
      newQuestionData.items = formState.items
      setLoading(true)
      await saveResponses(
        newQuestionData,
        'negative_media',
        !saved ? OPERATION_TYPES.CREATE : OPERATION_TYPES.UPDATE,
      )
        .then(async () => {
          await handleRemoveExcludedAttachments(attachmentsToRemove)
          setSaved(true)
          setEdited(false)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  const buildActivity = (activityInfo) => {
    return (
      <React.Fragment>
        <p>
          <span>Assunto: </span>
          {activityInfo.subject}
        </p>
        <p>
          <span>Link da noticia: </span>
          {activityInfo.media_link}
        </p>
        {activityInfo.envolvement_resume !== '' ? (
          <p>
            <span>Resumo do envolvimento na noticia: </span>
            {activityInfo.envolvement_resume}
          </p>
        ) : null}
        {activityInfo.attachments.length > 0 ? (
          <AttachmentsList attachments={activityInfo.attachments} />
        ) : null}
      </React.Fragment>
    )
  }

  useEffect(() => {
    const hasChanged =
      hasAnswerChanged(questionData.items, formState.items) ||
      formState.hasNegativeMedia !== questionData.items.length > 0
    setEdited(hasChanged)
    if (formState.items.length > 0 && formState.hasNegativeMedia === false) {
      toast.warn(
        'Você deve excluir as atividades antes de atualizar as respostas.',
      )
    }
  }, [formState])

  useEffect(() => {
    setSaved(questionData.has_answer)
    setFormState(initializeFormState(questionData))
  }, [questionData])
  
  useEffect(() => {
    if (hasAnswerChanged(questionData.items, formState.items))
      validateAndSave(true)
  }, [formState.items])

  return (
    <div className={classes.root}>
      <div className={classes.formContainer}>
        <h3>Questão 16</h3>
        <div className={classes.form}>
          <FormControl>
            <FormLabel>
              Já esteve envolvido em alguma situação de repercurssão em mídia,
              no âmbito nacional ou internacional? (televisão, jornal, redes de
              internet, etc){' '}
            </FormLabel>
            <RadioGroup
              row
              value={formState.hasNegativeMedia ? 'yes' : 'no'}
              name="hasRelations"
              onChange={handleRadioChange}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Sim" />
              <FormControlLabel value="no" control={<Radio />} label="Não" />
            </RadioGroup>
          </FormControl>

          {formState.hasNegativeMedia ? (
            <div className={classes.formFields}>
              <TextField
                id="subject"
                label="Assunto"
                onChange={handleFieldChange}
                fullWidth
                required
                value={activityData.subject}
                error={activityErrors.subject}
                size="small"
              />
              <TextField
                id="media_link"
                label="Link para a mídia"
                onChange={handleFieldChange}
                required
                fullWidth
                value={activityData.media_link}
                error={activityErrors.media_link}
                size="small"
              />

              <TextField
                id="envolvement_resume"
                label="Resumo do envolvimento na notícia"
                onChange={handleFieldChange}
                required={activityData.attachments.length === 0}
                fullWidth
                multiline
                value={activityData.envolvement_resume}
                error={activityErrors.envolvement_resume}
                size="small"
              />

              <AddAttachments
                targetFilename={`${formOwner?.prospect?.person?.cpf}-Comprovante_repercussao_midia`}
                title="Anexe um resumo da mídia: "
                attachments={activityData.attachments}
                updateAttachments={handleUpdateAttachments}
                error={activityErrors.attachments}
              />

              <div className={classes.fabContainer}>
                <Tooltip title="Adicionar atividade">
                  <Fab
                    onClick={handleValidateAndUpdateState}
                    disabled={formState?.items?.length === 10}
                    size="small"
                    color="primary"
                  >
                    <Add />
                  </Fab>
                </Tooltip>
              </div>
            </div>
          ) : null}
        </div>
        {formState?.items?.length > 0 ? (
          <div className={classes.answersContainer}>
            {addKeyToObjectsArray(formState.items)?.map((activity, index) => (
              <AnswerCard
                key={activity.key}
                index={index}
                handleRemove={handleRemoveActivity}
                mustRemove={formState.hasNegativeMedia === false}
              >
                {buildActivity(activity)}
              </AnswerCard>
            ))}
          </div>
        ) : null}
      </div>
      <div className={classes.buttonsContainer}>
        <QuestionButton
          variant="contained"
          onClick={validateAndSave}
          loading={loading}
          saved={saved}
          edited={edited}
          disabled={
            (formState.hasNegativeMedia && formState.items.length === 0) ||
            (saved && !edited)
          }
        />
      </div>
    </div>
  )
}
